import { app, translate } from "../app";
import { Loader } from "./loader";
import { languages } from "../locale";
import { Maps } from "./map";
import { ServicesList } from "./services";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { translations } from "../translations";
import { Container } from "./container";
import { useAnimateOnScroll } from "./useAnimateOnScroll";
import {
  CloseOutlined,
  FilterOutlined,
  ListAltOutline,
  MapSharp,
  Messages,
  Pencil,
} from "./icons";
import { Button } from "./button";
import classNames from "classnames";
import { SplitText } from "./splittext";
import { ChatbotPanel } from "./chatbot-panel";
import { AgentServiceFilters } from "../api";
import { filterServicesByAgentFilters } from "../agent-service-filters";
import {
  describeActiveFilters,
  isMapAnalyticsEnabled,
  MapFilterType,
  trackMapFilterApplied,
  trackZeroResults,
} from "../map-analytics";

import TreeSelect, { MenuItem } from "./tree-select";
import { useUserLocation } from "./useUserLocation";

enum filterType {
  serviceTypes = "serviceTypes",
  provider = "provider",
  populations = "Populations",
  accessibility = "Accessibility",
  location = "location",
}

type FilterValues = {
  serviceTypes: (string | number)[];
  provider: (string | number)[];
  Populations: (string | number)[];
  Accessibility: (string | number)[];
  location: (string | number)[];
};

const defaultFilterValues: FilterValues = {
  serviceTypes: [-1],
  provider: [-1],
  Populations: [-1],
  Accessibility: [-1],
  location: [-1],
};

function isAskAiDisabledByQuery(searchParams: URLSearchParams): boolean {
  const hideAI = searchParams.get("hideAI")
  if (hideAI != null) {
    return (
      hideAI === "" ||
      hideAI === "1" ||
      hideAI.toLowerCase() === "true" ||
      hideAI.toLowerCase() === "yes"
    )
  }

  const showIA = searchParams.get("showIA")
  return showIA === "0" || showIA?.toLowerCase() === "false"
}

export function BlockServices(props: { block: BlockServices }) {
  const { block } = props;

  const [filterOpen, setFilterOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const isAskAIEnabled =
    Boolean(block?.components?.showIA) &&
    !isAskAiDisabledByQuery(searchParams);

  type PanelMode = "filters" | "Chat";
  // Default to the Ask AI panel when it's enabled; otherwise fall back to filters.
  const [panelMode, setPanelMode] = useState<PanelMode>(
    isAskAIEnabled ? "Chat" : "filters",
  );
  const [pendingAIMessage, setPendingAIMessage] = useState<string | null>(null);
  const [hideCompanyCards, setHideCompanyCards] = useState(true);
  const [aiRecommendedServices, setAiRecommendedServices] = useState<
    Service[] | null
  >(null);
  const [aiServiceFilters, setAiServiceFilters] =
    useState<AgentServiceFilters | null>(null);
  const clearAiMapFilters = useCallback(() => {
    setAiServiceFilters(null);
    setAiRecommendedServices(null);
  }, []);

  useEffect(() => {
    if (!isAskAIEnabled) {
      setPanelMode("filters");
      setPendingAIMessage(null);
      clearAiMapFilters();
    }
  }, [isAskAIEnabled, clearAiMapFilters]);

  const [serviceToNavigate, setServiceToNavigate] = useState<Service | null>(
    null,
  );
  const userLocation = useUserLocation(isAskAIEnabled);
  useAnimateOnScroll();

  const isRTL = languages[app.locale]?.rtl;

  const {
    state: { servicesLoaded },
  } = app;
  const [selectedFilterValues, setSelectedFilterValues] =
    useState<FilterValues>(defaultFilterValues);
  const [lastValue, setLastValue] = useState<number[]>([-1]);
  const [view, setView] = useState<number>(0);

  const hideProviders = Boolean(block?.components?.hideproviders);

  const services = useMemo(
    () =>
      Object.values(app.data.services).filter((x) => x.status !== "archived"),
    [app.data.services, servicesLoaded],
  );
  const providers = useMemo(
    () =>
      hideProviders ? [] : Object.values(app.data.providers),
    [app.data.providers, hideProviders],
  );

  // Derive the used-category/subcategory sets and the filtered category/subcategory
  // lists once per services change instead of on every render.
  const { categories, subcategories } = useMemo(() => {
    const usedCategoryIdsSet = new Set<number>();
    const usedSubcategoryIds = new Set<number>();

    services.forEach((service) => {
      service.subcategories?.forEach((subcategory) => {
        usedSubcategoryIds.add(subcategory);
      });
      service.categories?.forEach((category) => {
        usedCategoryIdsSet.add(category);
      });
    });

    return {
      categories: Object.values(app.data.categories.categories || []).filter(
        (category) => usedCategoryIdsSet.has(category.id),
      ),
      subcategories: Object.values(
        app.data.categories.subCategories || [],
      ).filter((subcat) => usedSubcategoryIds.has(subcat.id)),
    };
  }, [services, app.data.categories]);

  const accessibilities = Object.values(app.data.categories.accesibility) || [];
  const populations = Object.values(app.data.categories.populations) || [];
  const combineRegionsWithCities = useMemo(() => {
    const regions = Object.values(app.regions) || [];
    const cities = Object.values(app.cities) || [];
    const combinedData: MenuItem[] = regions.map((region) => {
      const citiesForRegion = cities
        .filter((city) => city.region === region.id)
        .map((city) => ({
          value: city.id,
          label: translate(city.name),
        }));
      citiesForRegion.sort((a, b) => a.label.localeCompare(b.label));
      return {
        value: region.id,
        label: translate(region.name),
        children: citiesForRegion,
      };
    });
    combinedData.sort((a, b) =>
      String(a.label).localeCompare(String(b.label)),
    );
    combinedData.unshift({
      value: -1,
      label: translate(translations.allLocations),
    });
    return combinedData;
  }, [app.regions, app.cities, app.locale]);

  const combineCategoriesWithSubcategories = () => {
    const combinedData: MenuItem[] = categories.map((category) => {
      const subcategoriesForCategory = subcategories
        .filter((subcategory) => subcategory.parent.includes(category.id))
        .map((subcategory) => ({
          value: subcategory.id,
          label: translate(subcategory.name),
        }));
      return {
        value: category.id,
        label: translate(category.name),
        children: subcategoriesForCategory,
      };
    });
    combinedData.unshift({
      value: -1,
      label: translate(translations.allServiceTypes),
    });
    return combinedData;
  };

  const mapData = <T extends { id?: number; name?: LocalizableText }>(
    items: T[],
    allLabel: LocalizableText,
  ): MenuItem[] => {
    const mappedItems = items.map((x) => ({
      value: x.id,
      label: translate(x.name),
    }));
    mappedItems.unshift({ value: -1, label: translate(allLabel) });
    return mappedItems;
  };

  const filterFirstSubArray = (valuesArray: number[]): number[] => {
    if (valuesArray[0] === -1) {
      return [...valuesArray.slice(1)];
    } else {
      return valuesArray;
    }
  };

  const handleSelectedFilters = (value: any[], filter: filterType) => {
    if (!value.length || value.flat()[value.flat().length - 1] === -1) {
      setSelectedFilterValues((prevValues) => ({
        ...prevValues,
        [filter]: [-1],
      }));
      searchParams.delete(filter);
    } else {
      let changedValues: number[] = [];
      if (filter === filterType.serviceTypes) {
        let prevValue = { ...selectedFilterValues };
        for (let v of value) {
          if (
            !prevValue.serviceTypes.some(
              (pv) => JSON.stringify(pv) === JSON.stringify(v),
            )
          ) {
            changedValues.push(v);
          }
        }
        setLastValue(changedValues);
        setSelectedFilterValues((prevValues) => ({
          ...prevValues,
          serviceTypes: filterFirstSubArray(value),
        }));
        searchParams.delete(filter);
      } else {
        setSelectedFilterValues((prevValues) => ({
          ...prevValues,
          [filter]: filterFirstSubArray(value),
        }));
        searchParams.delete(filter);
      }
      filterFirstSubArray(value).forEach((x) => {
        searchParams.append(filter, x.toString());
      });
    }
    setSearchParams(new URLSearchParams(searchParams));
  };

  const handleFilterChange = useCallback(
    (
      filterType: filterType,
      value: (string | number)[],
      services: Service[],
    ) => {
      if (!value.length || value[0] === -1) return services;
      const ids = new Set(value);
      return services.filter((service) => {
        // filterType is a dynamic key (enum value) used to look up a field on
        // the service; not every enum value is a static key of Service.
        const criteria = (service as Record<string, unknown>)[filterType];
        if (Array.isArray(criteria)) {
          return criteria.some((item) => ids.has(item));
        } else {
          return ids.has(criteria as string | number);
        }
      });
    },
    [lastValue],
  );

  const getFilteredProviders = useCallback(
    (services: Service[]) => {
      const uniqueProviderIds = new Set(
        services.flatMap((service) => service.provider),
      );
      return providers
        .filter((provider) => uniqueProviderIds.has(provider.id))
        .sort((a, b) => translate(a.name).localeCompare(translate(b.name)));
    },
    [providers],
  );

  const handleLocationTypeChange = useCallback(
    (value: number[] | (string | number)[], services: Service[]) => {
      const regionMap = new Map<number, boolean>();
      const cityMap = new Map<number, boolean>();

      if (!value.length || value.flat()[value.flat().length - 1] === -1) {
        return services;
      }

      for (const criteria of value) {
        if (typeof criteria === "number") {
          regionMap.set(criteria, true);
        } else {
          cityMap.set(+criteria.split("-")[1], true);
        }
      }

      return services.filter((data) => {
        return regionMap.has(data.regionId) || cityMap.has(data.cityId);
      });
    },
    [],
  );

  const handleServiceTypeChange = useCallback(
    (value: number[] | (string | number)[], services: Service[]) => {
      const categoryMap = new Map<number, boolean>();
      const subcategoryMap = new Map<number, boolean>();

      if (!value.length || value.flat()[value.flat().length - 1] === -1) {
        return services;
      }

      for (const criteria of value) {
        if (typeof criteria === "number") {
          categoryMap.set(criteria, true);
        } else {
          subcategoryMap.set(+criteria.split("-")[1], true);
        }
      }

      services = services.filter((data) => {
        return (
          data.categories.some((category) => categoryMap.has(category)) ||
          data.subcategories.some((subcategory) =>
            subcategoryMap.has(subcategory),
          )
        );
      });

      return services;
    },
    [lastValue],
  );

  const applySelectedFilters = useCallback(
    (sourceServices: Service[], excludeProvider = false) => {
      let filteredData = [...sourceServices];

      Object.entries(selectedFilterValues).forEach(([key, value]) => {
        if (excludeProvider && key === filterType.provider) {
          return;
        }

        if (key === filterType.provider && value.length && value[0] !== -1) {
          filteredData = handleFilterChange(
            filterType.provider,
            value,
            filteredData,
          );
        } else if (
          key === filterType.accessibility &&
          value.length &&
          value[0] !== -1
        ) {
          filteredData = handleFilterChange(
            filterType.accessibility,
            value,
            filteredData,
          );
        } else if (
          key === filterType.populations &&
          value.length &&
          value[0] !== -1
        ) {
          filteredData = handleFilterChange(
            filterType.populations,
            value,
            filteredData,
          );
        } else if (
          key === filterType.serviceTypes &&
          value.length &&
          value[0] !== -1
        ) {
          filteredData = handleServiceTypeChange(value, filteredData);
        } else if (
          key === filterType.location &&
          value.length &&
          value[0] !== -1
        ) {
          filteredData = handleLocationTypeChange(value, filteredData);
        }
      });

      if (aiServiceFilters) {
        filteredData = filterServicesByAgentFilters(
          filteredData,
          aiServiceFilters,
          {
            translateName: translate,
          },
        );
      }

      return filteredData;
    },
    [
      selectedFilterValues,
      aiServiceFilters,
      handleFilterChange,
      handleServiceTypeChange,
      handleLocationTypeChange,
    ],
  );

  const filteredServices = useMemo(
    () => applySelectedFilters(services),
    [services, applySelectedFilters],
  );

  const displayedServices = useMemo(() => {
    if (aiRecommendedServices && !aiServiceFilters) {
      return aiRecommendedServices;
    }
    return filteredServices;
  }, [filteredServices, aiRecommendedServices, aiServiceFilters]);

  const hasAiMapPin = Boolean(aiServiceFilters || aiRecommendedServices);

  const filteredProviders = useMemo(
    () =>
      hideProviders
        ? []
        : getFilteredProviders(applySelectedFilters(services, true)),
    [services, applySelectedFilters, getFilteredProviders, hideProviders],
  );

  const hasActiveFilters = useMemo(
    () =>
      Object.values(selectedFilterValues).some(
        (value) => value.length > 0 && value[0] !== -1,
      ),
    [selectedFilterValues],
  );

  const trackedFiltersRef = useRef<FilterValues | null>(null);
  const trackedZeroResultRef = useRef<string | null>(null);

  useEffect(() => {
    if (!servicesLoaded || !isMapAnalyticsEnabled()) return;

    const previous = trackedFiltersRef.current;
    trackedFiltersRef.current = selectedFilterValues;
    if (!previous) return;

    (Object.keys(selectedFilterValues) as MapFilterType[]).forEach((key) => {
      const nextValues = selectedFilterValues[key as keyof FilterValues];
      const prevValues = previous[key as keyof FilterValues];
      if (JSON.stringify(prevValues) === JSON.stringify(nextValues)) return;

      trackMapFilterApplied({
        filterType: key,
        values: nextValues,
        resultCount: filteredServices.length,
        view: view === 0 ? "map" : "list",
      });
    });
  }, [selectedFilterValues, filteredServices.length, servicesLoaded, view]);

  useEffect(() => {
    if (!servicesLoaded || !isMapAnalyticsEnabled()) return;
    if (!hasActiveFilters || aiServiceFilters) return;

    if (filteredServices.length > 0) {
      trackedZeroResultRef.current = null;
      return;
    }

    const activeFilters = describeActiveFilters(selectedFilterValues);
    if (trackedZeroResultRef.current === activeFilters) return;
    trackedZeroResultRef.current = activeFilters;

    trackZeroResults({
      searchType: "filters",
      activeFilters,
      view: view === 0 ? "map" : "list",
    });
  }, [
    filteredServices.length,
    hasActiveFilters,
    aiServiceFilters,
    selectedFilterValues,
    servicesLoaded,
    view,
  ]);

  const providerFilterItems = useMemo(
    () =>
      hideProviders
        ? []
        : mapData(filteredProviders, translations.allProviders),
    [filteredProviders, app.locale, hideProviders],
  );

  useEffect(() => {
    const categoriesParams = searchParams.getAll("serviceTypes");
    const paramsCategories = categoriesParams.map((cat) => {
      if (cat.indexOf("-") !== -1) {
        return cat;
      } else {
        return +cat;
      }
    });
    const providersParams = searchParams.getAll("provider").map((provider) => {
      return +provider;
    });
    const populationsParams = searchParams
      .getAll("Populations")
      .map((population) => {
        return +population;
      });
    const accessibilityParams = searchParams
      .getAll("Accessibility")
      .map((accessibility) => {
        return +accessibility;
      });
    const locationParams = searchParams.getAll("location");
    const paramsLocation = locationParams.map((location) => {
      if (location.indexOf("-") !== -1) {
        return location;
      } else {
        return +location;
      }
    });

    if (
      categoriesParams.length ||
      providersParams.length ||
      populationsParams.length ||
      accessibilityParams.length ||
      locationParams.length
    ) {
      const element = document.getElementById("service-map");
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
      }
    }

    const mapView = searchParams.get("view");

    if (mapView) setView(+mapView);

    setSelectedFilterValues({
      serviceTypes: paramsCategories.length ? paramsCategories : [-1],
      provider: providersParams.length ? providersParams : [-1],
      Populations: populationsParams.length ? populationsParams : [-1],
      Accessibility: accessibilityParams.length ? accessibilityParams : [-1],
      location: paramsLocation.length ? paramsLocation : [-1],
    });
  }, [servicesLoaded]);

  const handleViewChange = (e: { target: { value: number } }) => {
    const value = e.target.value;
    searchParams.delete("view");
    searchParams.append("view", value.toString());
    setSearchParams(new URLSearchParams(searchParams));
    setView(value);
  };

  const hideMapToggle = block.components?.hidemap || block.components?.hidelist;

  return (
    <Container
      block={block}
      className={`!pt-0 relative transition-all service-container  ${isRTL ? "rtl" : ""}`}
    >
      <h1
        id="service-map"
        className={`fade-up-0 mt-0 pt-8 md:pt-12 text-[36px] md:text-[44px] font-bold leading-[44px] md:leading-[56px] ${isRTL ? "text-right" : "text-left"}`}
        style={{ direction: isRTL ? "rtl" : "ltr" }}
        data-animation="animate__fadeInUp"
      >
        <SplitText text={translate(props.block.title)} />
      </h1>
      {props.block.subtitle && <h2
        className={`fade-up-1 text-neutral-icon font-inter text-[20px] font-normal leading-[32px] mt-0 mb-6 ${isRTL ? "text-right" : "text-left"}`}
        style={{ direction: isRTL ? "rtl" : "ltr" }}
        data-animation="animate__fadeInUp"
      >
        <SplitText text={translate(props.block.subtitle)} />
      </h2>}
      {servicesLoaded && (
        <div className="flex flex-col md:flex-row gap-8">
          {/* MOBILE FULL SCREEN MODAL */}

          <div
            className={`${!filterOpen && "hidden"} fixed inset-0 bg-white z-50 flex flex-col p-5 overflow-auto md:hidden`}
          >
            <div className="flex ml-auto mb-5">
              <CloseOutlined
                className="w-6 h-6 text-black cursor-pointer"
                onClick={() => setFilterOpen(false)}
              />
            </div>

            <div className="flex flex-col flex-grow min-h-0">
              {panelMode === "filters" && (
                <div className="flex flex-col flex-1 gap-4">
                  <h2 className="fade-up-2" data-animation="animate__fadeInUp">
                    {translate(translations.filters)}
                  </h2>
                  {!block?.components?.hideservicetype && (
                    <div
                      className="fade-up-3"
                      data-animation="animate__fadeInUp"
                    >
                      <TreeSelect
                        label={translate(translations.serviceTypes)}
                        items={combineCategoriesWithSubcategories()}
                        className="w-full overflow-hidden service-types-select"
                        onChange={(value) =>
                          handleSelectedFilters(value, filterType.serviceTypes)
                        }
                        value={selectedFilterValues.serviceTypes}
                        defaultValue={[-1]}
                      />
                    </div>
                  )}
                  <div className="fade-up-3" data-animation="animate__fadeInUp">
                    <TreeSelect
                      label={translate(translations.locations)}
                      items={combineRegionsWithCities}
                      className="w-full overflow-hidden"
                      onChange={(value) =>
                        handleSelectedFilters(value, filterType.location)
                      }
                      value={selectedFilterValues.location}
                      defaultValue={[-1]}
                    />
                  </div>
                  {!block?.components?.hideproviders && (
                    <div
                      className="fade-up-4"
                      data-animation="animate__fadeInUp"
                    >
                      <TreeSelect
                        label={translate(translations.provider)}
                        items={providerFilterItems}
                        className="w-full overflow-hidden"
                        onChange={(value) =>
                          handleSelectedFilters(value, filterType.provider)
                        }
                        value={selectedFilterValues.provider}
                        defaultValue={[-1]}
                      />
                    </div>
                  )}
                  {!block?.components?.hideaccesibility && (
                    <div
                      className="fade-up-4"
                      data-animation="animate__fadeInUp"
                    >
                      <TreeSelect
                        label={translate(translations.accessibilities)}
                        items={mapData(
                          accessibilities,
                          translations.allAccessibilities,
                        )}
                        className="w-full overflow-hidden"
                        onChange={(value) =>
                          handleSelectedFilters(value, filterType.accessibility)
                        }
                        value={selectedFilterValues.Accessibility}
                        defaultValue={[-1]}
                      />
                    </div>
                  )}
                  {!block?.components?.hidepopulation && (
                    <div
                      className="fade-up-4"
                      data-animation="animate__fadeInUp"
                    >
                      <TreeSelect
                        label={translate(translations.populations)}
                        items={mapData(
                          populations,
                          translations.allPopulations,
                        )}
                        className="w-full overflow-hidden"
                        onChange={(value) =>
                          handleSelectedFilters(value, filterType.populations)
                        }
                        value={selectedFilterValues.Populations}
                        defaultValue={[-1]}
                      />
                    </div>
                  )}
                </div>
              )}

              {isAskAIEnabled && panelMode === "Chat" && (
                <div
                  className="flex flex-col flex-1 min-h-0"
                  style={{
                    overscrollBehavior: "contain",
                    scrollbarGutter: "stable",
                  }}
                >
                  <ChatbotPanel
                    fill
                    initialMessage={pendingAIMessage}
                    onMessageConsumed={() => setPendingAIMessage(null)}
                    onChatResponse={() => setHideCompanyCards(false)}
                    onServicesFound={(services, filters) => {
                      setAiServiceFilters(filters ?? null);
                      setAiRecommendedServices(services.length ? services : null);
                      setHideCompanyCards(false);
                    }}
                    onCardClick={(service) => {
                      setServiceToNavigate(service);
                      setFilterOpen(false);
                    }}
                    allServices={services}
                    userLocation={userLocation}
                  />
                </div>
              )}
            </div>
          </div>

          {/* DESKTOP SIDEBAR */}
          <div className="hidden md:flex flex-col grow-[2] flex-1 min-w-0 gap-4">
            <div className="map-seg flex h-10 overflow-hidden mt-3.5 w-fit">
              {/* Ask AI (default) sits first */}
              {isAskAIEnabled && <div
                onClick={() => {
                  setPanelMode("Chat");
                  setFilterOpen(true);
                }}
                className={classNames(
                  "map-seg__opt px-4 cursor-pointer flex items-center transition-colors text-sm font-bold uppercase tracking-wide",
                  { "is-active": panelMode === "Chat" },
                )}
              >
                <div className="flex items-center gap-2">
                  <Messages className="text-xl" />
                  <div>{translate(translations.askAI)}</div>
                </div>
              </div>}

              {/* Filters */}
              <div
                onClick={() => {
                  setPanelMode("filters");
                  setFilterOpen(true);
                }}
                className={classNames(
                  "map-seg__opt px-4 cursor-pointer flex items-center transition-colors text-sm font-bold uppercase tracking-wide",
                  { "is-active": panelMode === "filters" },
                )}
              >
                <div className="flex items-center gap-2">
                  <FilterOutlined className="text-xl" />
                  <div>{translate(translations.filters)}</div>
                </div>
              </div>
            </div>

            {panelMode === "filters" && (
              <>
                <h2
                  className={`fade-up-2 text-2xl font-bold leading-normal mb-1 mt-0 ${isRTL ? "text-right" : "text-left"}`}
                  style={{ direction: isRTL ? "rtl" : "ltr" }}
                  data-animation="animate__fadeInUp"
                >
                  {translate(translations.filters)}
                </h2>
                {!block?.components?.hideservicetype && (
                  <div className="fade-up-3" data-animation="animate__fadeInUp">
                    <TreeSelect
                      label={translate(translations.serviceTypes)}
                      items={combineCategoriesWithSubcategories()}
                      className="w-full overflow-hidden service-types-select"
                      onChange={(value) =>
                        handleSelectedFilters(value, filterType.serviceTypes)
                      }
                      value={selectedFilterValues.serviceTypes}
                      defaultValue={[-1]}
                      isRTL={isRTL}
                    />
                  </div>
                )}
                <div className="fade-up-3" data-animation="animate__fadeInUp">
                  <TreeSelect
                    label={translate(translations.locations)}
                    items={combineRegionsWithCities}
                    className="w-full overflow-hidden"
                    onChange={(value) =>
                      handleSelectedFilters(value, filterType.location)
                    }
                    value={selectedFilterValues.location}
                    defaultValue={[-1]}
                    isRTL={isRTL}
                  />
                </div>
                {!block?.components?.hideproviders && (
                  <div className="fade-up-4" data-animation="animate__fadeInUp">
                    <TreeSelect
                      label={translate(translations.provider)}
                      items={providerFilterItems}
                      className="w-full overflow-hidden"
                      onChange={(value) =>
                        handleSelectedFilters(value, filterType.provider)
                      }
                      value={selectedFilterValues.provider}
                      defaultValue={[-1]}
                      isRTL={isRTL}
                    />
                  </div>
                )}
                {!block?.components?.hideaccesibility && (
                  <div className="fade-up-4" data-animation="animate__fadeInUp">
                    <TreeSelect
                      label={translate(translations.accessibilities)}
                      items={mapData(
                        accessibilities,
                        translations.allAccessibilities,
                      )}
                      className="w-full overflow-hidden"
                      onChange={(value) =>
                        handleSelectedFilters(value, filterType.accessibility)
                      }
                      value={selectedFilterValues.Accessibility}
                      defaultValue={[-1]}
                      isRTL={isRTL}
                    />
                  </div>
                )}
                {!block?.components?.hidepopulation && (
                  <div className="fade-up-4" data-animation="animate__fadeInUp">
                    <TreeSelect
                      label={translate(translations.populations)}
                      items={mapData(populations, translations.allPopulations)}
                      className="w-full overflow-hidden"
                      onChange={(value) =>
                        handleSelectedFilters(value, filterType.populations)
                      }
                      value={selectedFilterValues.Populations}
                      defaultValue={[-1]}
                      isRTL={isRTL}
                    />
                  </div>
                )}
              </>
            )}

            {/* Div conditional needed to retain chat context */}
            {isAskAIEnabled && (
              <div className={`${!(panelMode === "Chat") && "hidden"}`}>
                <ChatbotPanel
                  initialMessage={pendingAIMessage}
                  onMessageConsumed={() => setPendingAIMessage(null)}
                  onChatResponse={() => setHideCompanyCards(false)}
                  onServicesFound={(services, filters) => {
                    setAiServiceFilters(filters ?? null);
                    setAiRecommendedServices(services.length ? services : null);
                    setHideCompanyCards(false);
                  }}
                  onCardClick={(service) => {
                    setServiceToNavigate(service);
                  }}
                  allServices={services}
                  userLocation={userLocation}
                />
              </div>
            )}
          </div>

          <div className="grow-[3] flex-1 relative">
            <div className="flex mt-3.5 mb-3.5 items-center flex-wrap gap-2">
              <button
                type="button"
                onClick={() => {
                  setPanelMode("filters");
                  setFilterOpen(true);
                }}
                className="map-action-btn map-action-btn--ghost md:hidden"
              >
                <FilterOutlined className="text-xl" />
                <span>{translate(translations.filters)}</span>
              </button>
              {isAskAIEnabled && (
                <button
                  type="button"
                  onClick={() => {
                    setPanelMode("Chat");
                    setFilterOpen(true);
                  }}
                  className="map-action-btn md:hidden"
                >
                  <Messages className="text-xl" />
                  <span>{translate(translations.askAI)}</span>
                </button>
              )}
              {view === 0 && !block?.components?.hidemap && (
                <span className="hidden md:inline font-normal text-base leading-snug">
                  {translate(translations.showing)} {displayedServices.length}{" "}
                  {translate(translations.of)} {services.length}{" "}
                </span>
              )}
              {view === 0 && hasAiMapPin && isAskAIEnabled && (
                <Button
                  ghost
                  className="text-sm text-[#1e79b2] border-[#1e79b2] hover:bg-blue-50"
                  onClick={clearAiMapFilters}
                >
                  {translate(translations.clearAIFilters)}
                </Button>
              )}
              {!hideMapToggle && (
                <div
                  className={`flex text-black ${isRTL ? "mr-auto" : "ml-auto"} z-10`}
                >
                  <div className="map-seg flex h-10 overflow-hidden">
                    <div
                      className={classNames(
                        "map-seg__opt px-4 cursor-pointer flex items-center transition-colors text-sm font-bold uppercase tracking-wide",
                        { "is-active": view === 0 },
                      )}
                      onClick={() => handleViewChange({ target: { value: 0 } })}
                    >
                      <div className="flex items-center gap-2">
                        <MapSharp className="text-xl" />
                        <div>{translate(translations.map)}</div>
                      </div>
                    </div>
                    <div
                      className={classNames(
                        "map-seg__opt px-4 cursor-pointer flex items-center transition-colors text-sm font-bold uppercase tracking-wide",
                        { "is-active": view === 1 },
                      )}
                      onClick={() => handleViewChange({ target: { value: 1 } })}
                    >
                      <div className="flex items-center gap-2">
                        <ListAltOutline className="text-xl" />
                        <div>{translate(translations.list)}</div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {view === 0 && !block?.components?.hidemap && (
              <div className="md:hidden my-4 font-2xl font-bold">
                {translate(translations.showing)} {displayedServices.length} of{" "}
                {services.length}{" "}
              </div>
            )}
            <div className="fade-up-6" data-animation="animate__fadeInUp">
              {view === 0 && !block?.components?.hidemap && (
                <Maps
                  services={displayedServices}
                  latitude={block?.latitude}
                  longitude={block?.longitude}
                  zoom={3}
                  showCompanyCards={!hideCompanyCards}
                  recommendedServices={aiRecommendedServices}
                  serviceToNavigate={serviceToNavigate}
                  onNavigateComplete={() => setServiceToNavigate(null)}
                  showIA={isAskAIEnabled}
                  userLocation={userLocation}
                  onAISearch={(query) => {
                    setPanelMode("Chat");
                    setFilterOpen(true);
                    setPendingAIMessage(query);
                    setHideCompanyCards(true);
                    clearAiMapFilters();
                  }}
                />
              )}
              {(view === 1 || block?.components?.hidemap) &&
                !block?.components?.hidelist && (
                  <ServicesList
                    serviceCount={services?.length}
                    services={displayedServices}
                    userLocation={userLocation}
                  />
                )}
              {(app.country === 42 || app.country === 43) && (
                <a
                  href="https://airtable.com/appchfQOvsnpnHVGA/pagDy19FiLsiIhUU0/form"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-6 inline-flex items-center justify-center gap-2 no-underline text-white bg-[#EC5C2E] hover:bg-[#d04a22] font-semibold text-sm leading-5 px-5 py-2.5 rounded-md transition-colors"
                >
                  {translate(translations.addOrEditResource)}
                  <Pencil className="text-base" aria-hidden="true" />
                </a>
              )}
            </div>
          </div>
        </div>
      )}
      {!servicesLoaded && (
        <div className="flex items-center justify-center my-16">
          <Loader size={72} width={12} className="bg-gray-500" />
        </div>
      )}
    </Container>
  );
}
